/* eslint-disable @typescript-eslint/no-shadow */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { isEmpty, isNonNullish } from 'remeda'
import { gql, type TypedDocumentNode, useQuery } from '@apollo/client'
import { VariantsSlider } from '@components/VariantsSlider/VariantSlider'
import type { MediaImage } from '@shopify/hydrogen-react/storefront-api-types'
import type { Variant } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import { SerifTitle } from '@velasca/ui/serif-title'
import { getProductCardImage } from '@velasca/utils/get-product-card-image'
import { getVariantDot } from '@velasca/utils/get-variant-dot'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { useExchangeRates } from '../ExchangeRatesProvider'
import {
  Col,
  ColorSelectorSliderWrapper,
  Details,
  Head,
  InfoBlock,
  PriceCol,
  Row,
  StyledPairWith,
  StyledRelatedSoles,
  SwatchesWrapper,
} from './InfoblockHeader.styles'
import type {
  GetProductVariantsConnectionData,
  GetProductVariantsConnectionVariables,
  InfoBlockHeaderPresentationalProps,
  InfoBlockHeaderProps,
} from './InfoBlockHeader.types'

const flatVariantImages = (nodes: MediaImage[] = []) => {
  return nodes
    .filter(({ mediaContentType }) => mediaContentType === 'IMAGE')
    .map(({ image }) => image?.url || '')
}

const ColorSelectorSlider = ({
  variants,
  loading,
  currentVariant,
}: {
  variants: Variant[]
  loading: boolean
  currentVariant: Variant
}) => {
  if (loading) {
    return <Skeleton width="100%" height="9.5rem" />
  }

  if (!variants || (variants && isEmpty(variants))) {
    return
  }

  // kept the selected one as first variant
  const sortedVariants = variants.sort((variant) =>
    variant.handle === currentVariant.handle ? -1 : 0,
  )

  return (
    <ColorSelectorSliderWrapper>
      <VariantsSlider
        selectedVariant={currentVariant}
        title="Color"
        slidesPerView={3.65}
        variants={sortedVariants}
        breakpoints={{
          375: { slidesPerView: 4.45 },
          390: { slidesPerView: 4.6 },
          475: { slidesPerView: 5.6 },
          768: { slidesPerView: 5 },
        }}
      />
    </ColorSelectorSliderWrapper>
  )
}

export const InfoBlockHeaderPresentational = ({
  loading,
  variants,
  selectedVariant,
  price,
  rawPrice,
  compareAtPrice,
  rawCompareAtPrice,
  description,
  title,
  pairWithText,
  pairWithTitle,
  productType,
  seasonName,
  soleDesc,
  relatedSoles,
  sizeAndFit,
  isGiftCard = false,
}: InfoBlockHeaderPresentationalProps) => {
  const { t } = useTranslation('product')

  return (
    <InfoBlock $isGiftCard={isGiftCard}>
      <Head>
        <div>
          <Row>
            <Col>
              {productType && (
                <SansText
                  as={'div'}
                  size="sm"
                  color="gray"
                  fontWeight="500"
                  style={{
                    lineHeight: 'normal',
                    textTransform: 'uppercase',
                    marginBottom: '0.5rem',
                  }}
                >
                  {productType}
                </SansText>
              )}
              <SerifTitle size="sm" as="h1" color="primary">
                {title}
              </SerifTitle>
            </Col>
            <PriceCol>
              {compareAtPrice &&
                isNonNullish(rawCompareAtPrice) &&
                isNonNullish(rawPrice) &&
                rawCompareAtPrice > rawPrice && (
                  <SansText
                    strikethrough
                    as="span"
                    size="md"
                    color="gray"
                    dangerouslySetInnerHTML={{
                      __html: compareAtPrice,
                    }}
                  />
                )}
              {price && (
                <SansText
                  as="span"
                  size="lg"
                  fontWeight="500"
                  dangerouslySetInnerHTML={{ __html: price }}
                />
              )}
            </PriceCol>
          </Row>

          <Details>
            {description?.composition && (
              <SansText style={{ lineHeight: 1 }}>
                {description.composition}
              </SansText>
            )}
            {description?.html && (
              <SansText
                dangerouslySetInnerHTML={{
                  __html: description.html,
                }}
              />
            )}
            {soleDesc && (
              <SansText style={{ lineHeight: 1 }}>{soleDesc}</SansText>
            )}

            <SansText style={{ lineHeight: 1 }}>{seasonName}</SansText>

            {description?.color && (
              <SansText margin={'1rem 0 0 0'}>
                <SansText fontWeight="500" as={'span'}>
                  {t('color').charAt(0).toUpperCase() + t('color').slice(1)}:{' '}
                </SansText>
                {description.color}
              </SansText>
            )}
          </Details>
          <SwatchesWrapper>
            {variants && (
              <ColorSelectorSlider
                variants={variants}
                loading={loading}
                currentVariant={selectedVariant || variants[0]}
              />
            )}

            {relatedSoles && !isEmpty(relatedSoles) && (
              <StyledRelatedSoles soles={relatedSoles} />
            )}
          </SwatchesWrapper>
        </div>
      </Head>

      {sizeAndFit && (
        <SansText
          dangerouslySetInnerHTML={{
            __html: sizeAndFit,
          }}
        />
      )}

      {pairWithText && (
        <StyledPairWith title={pairWithTitle} paragraph={pairWithText} />
      )}
    </InfoBlock>
  )
}

const GET_PRODUCT_COLOR_VARIANTS: TypedDocumentNode<
  GetProductVariantsConnectionData,
  GetProductVariantsConnectionVariables
> = gql`
  query GetProductVariants($query: String) {
    products(first: 250, query: $query) {
      edges {
        node {
          id
          title
          handle
          variantColorStyle: metafield(
            namespace: "custom"
            key: "variant_color_style"
          ) {
            value
          }
          textureImageName: metafield(
            namespace: "custom"
            key: "texture_image_name"
          ) {
            value
          }
          colorDescription: metafield(namespace: "custom", key: "color") {
            value
          }
          materialShort: metafield(namespace: "custom", key: "material_short") {
            value
          }
          variantImage: metafield(
            namespace: "custom"
            key: "pattern_image_name"
          ) {
            value
          }
          frontImageIndex: metafield(
            namespace: "custom"
            key: "front_image_index"
          ) {
            value
          }
          customProductType: metafield(
            namespace: "custom"
            key: "product_type"
          ) {
            value
          }
          media(first: 50) {
            nodes {
              mediaContentType
              ... on MediaImage {
                image {
                  altText
                  url
                }
              }
            }
          }
          variantColors: metafield(namespace: "custom", key: "variant_colors") {
            value
          }
          soleColorHex: metafield(namespace: "custom", key: "sole_color_hex") {
            value
          }
        }
      }
    }
  }
`

const InfoBlockHeader = ({
  collectionBrandName,
  description,
  handle,
  rawPrice,
  rawCompareAtPrice,
  title,
  pairWithProducts,
  productType,
  seasonName,
  soleDesc,
  relatedSoles,
  sizeAndFit,
}: InfoBlockHeaderProps): JSX.Element => {
  const { loading, data, error } = useQuery(GET_PRODUCT_COLOR_VARIANTS, {
    variables: {
      query: `title:${title}`,
    },
  })

  const { t } = useTranslation('product')

  const { convert } = useExchangeRates()

  useEffect(() => {
    if (!error) {
      return
    }

    handleError(error, {
      message: "Couldn't fetch product variants",
      behaviour: 'throw',
    })
  }, [error])

  const variants: Variant[] =
    data?.products?.edges
      .filter(({ node: { title: variantTitle } }) => variantTitle === title)
      .map(
        ({
          node: {
            id,
            title,
            handle,
            variantColorStyle,
            textureImageName,
            variantImage,
            variantColors,
            materialShort,
            colorDescription,
            soleColorHex,
            media,
            customProductType,
            frontImageIndex,
          },
        }) => {
          return {
            id,
            title,
            handle,
            // same function used in the product card
            img: getProductCardImage({
              images: flatVariantImages(media?.nodes as MediaImage[]),
              customProductType: customProductType?.value
                ? parseInt(customProductType.value)
                : undefined,
              frontImageIndex: frontImageIndex?.value
                ? parseInt(frontImageIndex.value)
                : undefined,
            }),
            customProductType,
            description: {
              bold: colorDescription?.value ?? null,
              thin: materialShort?.value ?? null,
            },
            // variantDot has been deprecated during the redesing 2025. kept the logic for an eventually backward compatibility
            variantDot: getVariantDot({
              variantColors,
              soleColorHex,
              variantColorStyle,
              textureImageName,
              variantImage,
              imageAssetPublicPath: $settings.imageAssetPublicPath,
            }),
          }
        },
      ) || []

  const selectedVariant = variants?.find(
    ({ handle: variantHandle }) => variantHandle === handle,
  )

  return (
    <InfoBlockHeaderPresentational
      collectionBrandName={collectionBrandName}
      loading={loading}
      sizeAndFit={sizeAndFit}
      variants={variants}
      selectedVariant={selectedVariant}
      price={rawPrice ? convert(rawPrice) : undefined}
      rawPrice={rawPrice}
      rawCompareAtPrice={rawCompareAtPrice}
      description={description}
      title={title}
      pairWithText={pairWithProducts && t('pairWithText', pairWithProducts)}
      pairWithTitle={t('pairWithTitle')}
      productType={productType}
      seasonName={seasonName}
      soleDesc={soleDesc}
      handle={handle}
      relatedSoles={relatedSoles}
      compareAtPrice={
        rawCompareAtPrice ? convert(rawCompareAtPrice) : undefined
      }
    />
  )
}

export default InfoBlockHeader
